// ═══════════════════════════════════════════════════════════════════
//  useSavedSearches — Cross-device saved-query sync via Supabase.
// ═══════════════════════════════════════════════════════════════════
//
//  Reads + writes `public.saved_searches`. Falls back gracefully
//  when offline / unauthenticated by reading a localStorage cache,
//  so the UI never breaks.
//
//  Saved searches are *intentional*: the user clicked "Pin" to keep a
//  query around. Distinct from `recents` (LRU history in
//  SearchContext, localStorage-only).
//
// ═══════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { fromCustom } from '@/lib/supabaseTyped';
import { useAuth } from '@/contexts/AuthContext';
import type { SavedSearch, SearchScope } from '@/types/search';

const CACHE_KEY = 'fw-saved-searches-cache';

function loadCache(): SavedSearch[] {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? (parsed as SavedSearch[]) : [];
  } catch {
    return [];
  }
}

function writeCache(rows: SavedSearch[]): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(rows));
  } catch {
    // ignore
  }
}

interface DbRow {
  id: string;
  label: string;
  query: string;
  scope: string;
  pinned_at: string;
}

function dbRowToSaved(r: DbRow): SavedSearch {
  return {
    label: r.label,
    query: r.query,
    scope: (r.scope as SearchScope) ?? 'all',
    pinnedAt: new Date(r.pinned_at).getTime(),
    ranAt: 0,
    hitCount: 0,
  };
}

export interface SavedSearchesApi {
  saved: SavedSearch[];
  isLoading: boolean;
  pin: (entry: { label: string; query: string; scope: SearchScope }) => Promise<void>;
  unpin: (label: string) => Promise<void>;
}

export function useSavedSearches(): SavedSearchesApi {
  const { user } = useAuth();
  const [saved, setSaved] = useState<SavedSearch[]>(() => loadCache());
  const [isLoading, setIsLoading] = useState(false);

  // Initial fetch when the user becomes available.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setIsLoading(true);
    (async () => {
      const { data, error } = await fromCustom<DbRow>('saved_searches')
        .select('id, label, query, scope, pinned_at')
        .order('pinned_at', { ascending: false })
        .limit(1000);
      if (cancelled) return;
      if (!error && data) {
        const mapped = data.map(dbRowToSaved);
        setSaved(mapped);
        writeCache(mapped);
      }
      setIsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const pin = useCallback(
    async (entry: { label: string; query: string; scope: SearchScope }) => {
      const next: SavedSearch = {
        ...entry,
        pinnedAt: Date.now(),
        ranAt: 0,
        hitCount: 0,
      };
      // Optimistic update
      setSaved((prev) => {
        const filtered = prev.filter((s) => s.label !== entry.label);
        const updated = [next, ...filtered];
        writeCache(updated);
        return updated;
      });
      if (!user) return;
      const { error } = await fromCustom('saved_searches').upsert(
        {
          user_id: user.id,
          label: entry.label,
          query: entry.query,
          scope: entry.scope,
          pinned_at: new Date().toISOString(),
        },
        { onConflict: 'user_id,label' },
      );
      if (error) {
         
        console.warn('[saved-searches] pin failed', error.message);
      }
    },
    [user],
  );

  const unpin = useCallback(
    async (label: string) => {
      setSaved((prev) => {
        const updated = prev.filter((s) => s.label !== label);
        writeCache(updated);
        return updated;
      });
      if (!user) return;
      const client = supabase as unknown as {
        from: (t: string) => {
          delete: () => {
            eq: (c: string, v: unknown) => {
              eq: (c: string, v: unknown) => Promise<{ error: Error | null }>;
            };
          };
        };
      };
      const { error } = await client
        .from('saved_searches')
        .delete()
        .eq('user_id', user.id)
        .eq('label', label);
      if (error) {
         
        console.warn('[saved-searches] unpin failed', error.message);
      }
    },
    [user],
  );

  return { saved, isLoading, pin, unpin };
}
